import * as React from 'react'
import { Row, Col, Button, ButtonGroup } from 'reactstrap'

// Enhanced textarea editor with improved styling and features
const Editor = (props: any) => {
  const isReadOnly = props.options && props.options.readOnly
  return (
    <textarea
      style={{
        width: '100%',
        height: props.height,
        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
        fontSize: '14px',
        backgroundColor: isReadOnly ? '#f8f9fa' : '#1e1e1e',
        color: isReadOnly ? '#495057' : '#d4d4d4',
        border: '1px solid #ced4da',
        padding: '12px',
        resize: 'vertical',
        borderRadius: '4px',
        lineHeight: '1.5',
        outline: 'none',
        transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
      }}
      value={props.value}
      onChange={(e) => props.onChange && props.onChange(e.target.value)}
      readOnly={isReadOnly}
      placeholder={props.field === 'prompt' ? 'Enter your prompt template here...' : 
                  props.field === 'options' ? '{"nunjucks": true, "role": "user"}' :
                  props.field === 'description' ? 'Enter a brief description for this template...' :
                  props.field === 'sequence' ? '1' : ''}
      onFocus={(e) => {
        if (!isReadOnly) {
          e.target.style.borderColor = '#80bdff'
          e.target.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.25)'
        }
      }}
      onBlur={(e) => {
        e.target.style.borderColor = '#ced4da'
        e.target.style.boxShadow = 'none'
      }}
      spellCheck={false}
    />
  )
}

interface EditablePromptTemplate {
  id: number
  sequence: number
  prompt: string
  options: string
  description?: string
  changedFields: string[]
  invalidFields: string[]
  isNunjucks?: boolean
}

interface PromptTemplateEditorProps {
  template: EditablePromptTemplate
  onChange: (template: EditablePromptTemplate) => void
  readOnly?: boolean
  field: string // 'prompt', 'sequence', 'options', 'description'
}

interface PromptTemplateEditorState {
  editorMode: 'text' | 'nunjucks' | 'json'
  validationErrors: string[]
  editorValue: string
}

export class PromptTemplateEditor extends React.Component<PromptTemplateEditorProps, PromptTemplateEditorState> {

  constructor(props: PromptTemplateEditorProps) {
    super(props)
    
    const isNunjucks = this.isNunjucksTemplate(props.template)
    const editorMode = this.getEditorMode(props.field, isNunjucks)
    
    this.state = {
      editorMode,
      validationErrors: [],
      editorValue: props.template[props.field] || ''
    }
  }

  componentDidUpdate(prevProps: PromptTemplateEditorProps) {
    if (prevProps.template[this.props.field] !== this.props.template[this.props.field]) {
      this.setState({
        editorValue: this.props.template[this.props.field] || ''
      })
    }
  }

  private isNunjucksTemplate = (template: EditablePromptTemplate): boolean => {
    try {
      const options = JSON.parse(template.options || '{}')
      return options.nunjucks === true
    } catch (e) {
      return false
    }
  }

  private getEditorMode = (field: string, isNunjucks: boolean): 'text' | 'nunjucks' | 'json' => {
    if (field === 'options') return 'json'
    if (field === 'prompt' && isNunjucks) return 'nunjucks'
    return 'text'
  }

  private getEditorLanguage = (): string => {
    switch (this.state.editorMode) {
      case 'json': return 'json'
      case 'nunjucks': return 'html' // Use HTML for Nunjucks syntax highlighting
      default: return 'plaintext'
    }
  }

  private validateContent = (value: string): string[] => {
    const errors: string[] = []
    
    if (this.props.field === 'options') {
      try {
        const parsed = JSON.parse(value || '{}')
        // Validate that it's an object, not a primitive
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          errors.push('Options must be a JSON object (not array or primitive)')
        }
      } catch (e) {
        // Provide more specific error messages
        const errorMessage = e.message
        if (errorMessage.includes('Unexpected token')) {
          errors.push(`Invalid JSON syntax: ${errorMessage}`)
        } else if (errorMessage.includes('Unexpected end')) {
          errors.push('Incomplete JSON - check for missing brackets or quotes')
        } else {
          errors.push(`JSON parse error: ${errorMessage}`)
        }
      }
    }

    if (this.props.field === 'sequence') {
      const num = parseInt(value, 10)
      if (isNaN(num) || num < 0) {
        errors.push('Sequence must be a valid positive number')
      }
    }

    if (this.props.field === 'prompt' && (!value || value.trim().length === 0)) {
      errors.push('Prompt cannot be empty')
    }

    if (this.props.field === 'description' && (!value || value.trim().length === 0)) {
      errors.push('Description is required')
    }

    if (this.props.field === 'description' && value && value.length > 500) {
      errors.push('Description must be 500 characters or less')
    }

    return errors
  }

  private handleEditorChange = (value: string | undefined) => {
    const newValue = value || ''
    const validationErrors = this.validateContent(newValue)
    
    this.setState({
      editorValue: newValue,
      validationErrors
    })

    // Update the template
    const updatedTemplate = {
      ...this.props.template,
      [this.props.field]: newValue
    }

    // Update changed fields
    if (!updatedTemplate.changedFields.includes(this.props.field)) {
      updatedTemplate.changedFields = [...updatedTemplate.changedFields, this.props.field]
    }

    // Update invalid fields
    if (validationErrors.length > 0) {
      if (!updatedTemplate.invalidFields.includes(this.props.field)) {
        updatedTemplate.invalidFields = [...updatedTemplate.invalidFields, this.props.field]
      }
    } else {
      updatedTemplate.invalidFields = updatedTemplate.invalidFields.filter(f => f !== this.props.field)
    }

    this.props.onChange(updatedTemplate)
  }

  private handleEditorMount = (editor: any, monaco: any) => {
    // Placeholder for Monaco setup when real editor is integrated
  }

  private formatJSON = () => {
    try {
      const parsed = JSON.parse(this.state.editorValue || '{}')
      const formatted = JSON.stringify(parsed, null, 2)
      this.handleEditorChange(formatted)
    } catch (error) {
      // Invalid JSON - don't format
    }
  }

  private minifyJSON = () => {
    try {
      const parsed = JSON.parse(this.state.editorValue || '{}')
      const minified = JSON.stringify(parsed)
      this.handleEditorChange(minified)
    } catch (error) {
      // Invalid JSON - don't minify
    }
  }

  private getEditorHeight = (): string => {
    if (this.props.field === 'prompt') {
      return this.state.editorMode === 'nunjucks' ? '400px' : '200px'
    }
    if (this.props.field === 'options') {
      return '150px'
    }
    if (this.props.field === 'description') {
      return '80px'
    }
    return '50px' // sequence field
  }

  render() {
    const { readOnly, field } = this.props
    const { validationErrors, editorValue } = this.state
    const hasErrors = validationErrors.length > 0

    return (
      <div className="prompt-template-editor">
        <Row>
          <Col>
            {field === 'prompt' && this.state.editorMode === 'nunjucks' && (
              <div className="editor-info mb-2">
                <small className="text-info">
                  <i className="fa fa-info-circle"></i> Nunjucks template detected. 
                  Use <code>{`{{ variable }}`}</code> for variables, <code>{`{% tag %}`}</code> for logic.
                </small>
              </div>
            )}

            {field === 'options' && !readOnly && (
              <div className="json-controls mb-2 d-flex justify-content-end">
                <ButtonGroup size="sm">
                  <Button
                    color="secondary"
                    outline
                    onClick={this.formatJSON}
                    disabled={validationErrors.length > 0}
                    title="Format JSON with proper indentation"
                  >
                    <i className="fa fa-indent"></i> Format
                  </Button>
                  <Button
                    color="secondary"
                    outline
                    onClick={this.minifyJSON}
                    disabled={validationErrors.length > 0}
                    title="Minify JSON to single line"
                  >
                    <i className="fa fa-compress"></i> Minify
                  </Button>
                </ButtonGroup>
              </div>
            )}
            
            <div className={`editor-container ${hasErrors ? 'has-error' : ''}`}>
              <Editor
                height={this.getEditorHeight()}
                language={this.getEditorLanguage()}
                value={editorValue}
                theme={this.state.editorMode === 'nunjucks' ? 'nunjucks-theme' : 'vs-dark'}
                onChange={this.handleEditorChange}
                onMount={this.handleEditorMount}
                options={{
                  readOnly,
                  minimap: { enabled: field === 'prompt' },
                  wordWrap: 'on',
                  lineNumbers: field === 'prompt' ? 'on' : 'off',
                  folding: field === 'prompt',
                  scrollBeyondLastLine: false,
                  fontSize: 14,
                  fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                  automaticLayout: true,
                  bracketPairColorization: { enabled: true },
                  suggest: {
                    showKeywords: true,
                    showSnippets: true
                  }
                }}
              />
            </div>

            {hasErrors && (
              <div className="validation-errors mt-2">
                {validationErrors.map((error, index) => (
                  <div key={index} className="text-danger small">
                    <i className="fa fa-exclamation-triangle"></i> {error}
                  </div>
                ))}
              </div>
            )}
          </Col>
        </Row>
      </div>
    )
  }
}